import React, { useState, useRef, useEffect } from 'react';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../hooks/useAuth';
import { useViewMode } from '../contexts/ViewModeContext';
import { Client } from '../types';
import { AddClientModal } from './AddClientModal';
import { EditClientModal } from './EditClientModal';
import { useHasPermission } from './auth/PermissionGate';

interface ClientSelectorProps {
  selectedClient?: Client | null;
  onClientChange?: (client: Client | null) => void;
}

export const ClientSelector: React.FC<ClientSelectorProps> = ({ selectedClient: propSelectedClient, onClientChange }) => {
  const { selectedClient, availableClients, setSelectedClient, isLoading, error, refreshClients } = useClient();
  const { user } = useAuth();
  const { isAdminView } = useViewMode();
  // Use view mode for admin features (allows agent@botpros.ai to toggle between admin/staff views)
  //
  // isAdminView is a DEVELOPER PREVIEW FLAG, not authorization: ViewModeContext defaults
  // every user to 'admin' and only the developer email can toggle it. Authorization comes
  // from the permission layer, which the backend enforces on POST/PUT /api/clients. Each
  // client-write affordance below is therefore gated on `isAdmin && <permission>`: the
  // preview toggle still hides admin chrome for the developer, and for everyone else
  // (always admin view) the permission is the effective gate. Admins hold clients.* by
  // role default, so their UI is unchanged.
  const isAdmin = isAdminView;
  const canCreateClient = useHasPermission('clients.create');
  const canEditClient = useHasPermission('clients.edit');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [hoveredClientId, setHoveredClientId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredClients = availableClients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectClient = (client: Client) => {
    if (onClientChange) {
      // Modal usage - just update state, don't reload
      onClientChange(client);
      setIsOpen(false);
      setSearchTerm('');
    } else {
      // Main app usage - update context and reload to refresh all data
      setSelectedClient(client);
      setIsOpen(false);
      setSearchTerm('');
      window.location.reload();
    }
  };

  const handleAddClient = () => {
    setIsOpen(false);
    setShowAddModal(true);
  };

  const handleClientAdded = async (client: Client) => {
    // Refresh the client list
    if (refreshClients) {
      await refreshClients();
    }
    // Select the newly added client
    handleSelectClient(client);
  };

  const handleEditClient = (client: Client, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingClient(client);
    setShowEditModal(true);
    setIsOpen(false);
  };

  const handleClientUpdated = async (client: Client) => {
    // Refresh the client list
    if (refreshClients) {
      await refreshClients();
    }
    // If this is the currently selected client, update it
    if (currentClient?.id === client.id) {
      handleSelectClient(client);
    }
  };

  const handleClientDeleted = async (clientId: string) => {
    // Refresh the client list
    if (refreshClients) {
      await refreshClients();
    }
    // If the deleted client was selected, clear selection
    if (currentClient?.id === clientId) {
      if (onClientChange) {
        onClientChange(null);
      } else {
        setSelectedClient(null);
      }
    }
    window.location.reload();
  };

  // Use prop if provided, otherwise use context
  const currentClient = propSelectedClient !== undefined ? propSelectedClient : selectedClient;

  const getClientInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getClientColor = (name: string) => {
    const palette: Record<string, string> = {
      'ALTRU': 'bg-blue-500',
      'MEDSCAN': 'bg-green-500',
      'CROSSPOINTE': 'bg-purple-500',
      'SERVIUM': 'bg-pink-500',
      'LIVE IN MOTION': 'bg-indigo-500',
      'MOCAN': 'bg-teal-500'
    };

    const normalized = name.trim().toUpperCase();
    if (palette[normalized]) {
      return palette[normalized];
    }

    const fallbacks = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500'
    ];
    const index = name.charCodeAt(0) % fallbacks.length;
    return fallbacks[index];
  };

  const showLoadingState = isLoading && !currentClient && availableClients.length === 0;

  if (showLoadingState) {
    return (
      <div className="animate-pulse">
        <div className="h-10 w-48 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-sm">
        <i className="fas fa-exclamation-triangle mr-2"></i>
        {error}
      </div>
    );
  }

  if (availableClients.length === 0) {
    return (
      <>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-sm">No clients available</span>
          {isAdmin && canCreateClient && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <i className="fas fa-plus text-xs"></i>
              Create Client
            </button>
          )}
        </div>

        {/* Add Client Modal */}
        <AddClientModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onClientAdded={handleClientAdded}
        />
      </>
    );
  }

  return (
    <div className="relative flex justify-center" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center space-x-3 px-4 py-1.5 rounded-lg border transition-all
          ${currentClient
            ? 'bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
            : 'bg-yellow-50 border-yellow-400 animate-pulse'
          }
        `}
      >
        {currentClient ? (
          <>
            <div className={`w-7 h-7 rounded-full ${getClientColor(currentClient.name)} flex items-center justify-center text-white text-xs font-bold`}>
              {getClientInitials(currentClient.name)}
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900">{currentClient.name}</div>
              {currentClient.code && (
                <div className="text-xs text-gray-500">{currentClient.code}</div>
              )}
            </div>
            {isLoading && isOpen && (
              <i className="fas fa-circle-notch animate-spin text-blue-400 text-xs" aria-hidden="true"></i>
            )}
            <i className="fas fa-chevron-down text-gray-400 ml-2"></i>
          </>
        ) : (
          <div className="flex items-center space-x-2">
            <i className="fas fa-exclamation-circle text-yellow-500"></i>
            <span className="text-sm font-medium text-gray-700">Select a Client</span>
            <i className="fas fa-chevron-down text-gray-400"></i>
          </div>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 z-50 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
          {/* Search Bar */}
          {availableClients.length > 3 && (
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search clients..."
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
                <i className="fas fa-search absolute left-3 top-2.5 text-gray-400"></i>
              </div>
            </div>
          )}

          {/* Client List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredClients.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No clients found
              </div>
            ) : (
              filteredClients.map((client) => (
                <div
                  key={client.id}
                  className={`
                    relative flex items-center space-x-3 px-4 py-3 hover:bg-blue-50 transition-colors
                    ${currentClient?.id === client.id ? 'bg-blue-100' : ''}
                  `}
                  onMouseEnter={() => setHoveredClientId(client.id)}
                  onMouseLeave={() => setHoveredClientId(null)}
                >
                  <button
                    onClick={() => handleSelectClient(client)}
                    className="flex-1 flex items-center space-x-3"
                  >
                    <div className={`w-10 h-10 rounded-full ${getClientColor(client.name)} flex items-center justify-center text-white text-sm font-bold`}>
                      {getClientInitials(client.name)}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-gray-900">{client.name}</div>
                      {client.code && (
                        <div className="text-xs text-gray-500">{client.code}</div>
                      )}
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    {currentClient?.id === client.id && (
                      <i className="fas fa-check text-blue-500"></i>
                    )}
                    {isAdmin && canEditClient && hoveredClientId === client.id && (
                      <button
                        onClick={(e) => handleEditClient(client, e)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Edit client"
                      >
                        <i className="fas fa-cog"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200">
            {availableClients.length > 5 && (
              <div className="px-4 py-2">
                <div className="text-xs text-gray-500">
                  {filteredClients.length} of {availableClients.length} clients
                </div>
              </div>
            )}
            {isAdmin && canCreateClient && (
              <button
                onClick={handleAddClient}
                className="w-full px-4 py-3 flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors border-t border-gray-200"
              >
                <i className="fas fa-plus-circle text-blue-600"></i>
                <span className="text-sm font-medium text-blue-600">Add New Client</span>
              </button>
            )}
          </div>
        </div>
      )}

      <AddClientModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onClientAdded={handleClientAdded}
      />

      <EditClientModal
        isOpen={showEditModal}
        client={editingClient}
        onClose={() => {
          setShowEditModal(false);
          setEditingClient(null);
        }}
        onClientUpdated={handleClientUpdated}
        onClientDeleted={handleClientDeleted}
      />
    </div>
  );
};